"use client";

import { useUser } from "@clerk/clerk-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Reveal from "@/components/ui/Reveal";
import FaqSection, { type FaqItem } from "@/components/ui/FaqSection";
import { PRICING_CHECKOUT_ENABLED, PRICING_CHECKOUT_OFF_LABEL } from "@/lib/payments/checkout-availability";
import { BOOK_DEMO_HREF } from "@/lib/marketing-links";
import { CATALOG_COURSES, CATALOG_PRICE, catalogCourseFacts } from "@/lib/data/catalog-courses";
import { DEFAULT_CATALOG_MODULE_COUNTS, type CatalogModuleCounts } from "@/lib/data/catalog-module-counts";
import * as api from "@/src/api/sdk.gen";
import { formatCatalogPrice } from "@/lib/catalog/use-course-prices";

const PRICING_FAQS: FaqItem[] = [
    {
        question: "What do I get for the $7?",
        answer: "The course you choose: every level of it, and its certificate when you complete it.",
    },
    {
        question: "Which course should I buy?",
        answer: "Whichever you want to start with. Every course is $7, and each one is bought on its own.",
    },
    {
        question: "Is this a subscription?",
        answer: "No. It's a single one-time payment. There's no recurring charge and nothing to cancel.",
    },
    {
        question: "Do I keep the course I bought?",
        answer: "Yes. The course you buy is yours: one payment covers every level of it.",
    },
    {
        question: "How do I pay?",
        answer:
            "You'll be prompted to sign in, then complete payment through the secure checkout flow we use across the platform.",
    },
];

const TRUST_STRIP = [
    { icon: "check_circle", label: "One-time payment" },
    { icon: "lock", label: "Secure checkout" },
    { icon: "build", label: "Built by atomcamp Arabia" },
] as const;

type EntryPlan = {
    key: string;
    courseTitle: string;
    planId: number | null;
    priceText: string;
    alreadyOwned: boolean;
};

export default function Pricing({ moduleCounts = DEFAULT_CATALOG_MODULE_COUNTS }: { moduleCounts?: CatalogModuleCounts }) {
    const router = useRouter();
    const { isSignedIn } = useUser();
    const [plansByKey, setPlansByKey] = useState<Record<string, EntryPlan>>({});

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const { data } = await api.getEntryLevelPlansApiV1PaymentsEntryLevelPlansGet();
                if (cancelled || !data?.plans) return;
                const map: Record<string, EntryPlan> = {};
                for (const p of data.plans) {
                    map[p.key] = {
                        key: p.key,
                        courseTitle: p.course_title,
                        planId: p.plan_id ?? null,
                        priceText:
                            p.price != null && p.currency ? formatCatalogPrice(p.price, p.currency) : CATALOG_PRICE,
                        alreadyOwned: p.already_owned ?? false,
                    };
                }
                setPlansByKey(map);
            } catch {
                if (!cancelled) setPlansByKey({});
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isSignedIn]);

    const resolvePlan = (offerPlanKey: string) => {
        if (offerPlanKey === "agentic" || offerPlanKey === "professionals") {
            return plansByKey["professionals"] ?? plansByKey["agentic"] ?? null;
        }
        return plansByKey[offerPlanKey] ?? null;
    };

    const handleGetStarted = (planKey: string) => {
        if (!PRICING_CHECKOUT_ENABLED) return;
        const plan = resolvePlan(planKey);
        const planId = plan?.planId;

        if (!isSignedIn) {
            const redirectUrl = planId ? `/checkout/${planId}` : "/pricing";
            router.push(`/sign-in?redirect_url=${encodeURIComponent(redirectUrl)}`);
            return;
        }

        if (planId != null) {
            router.push(`/checkout/${planId}`);
        } else {
            router.push("/catalog");
        }
    };

    return (
        <section className="relative overflow-hidden bg-white">
            <div className="px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto">
                <Reveal className="py-size-xl max-w-3xl mx-auto text-center">
                    <span className="font-label-md text-label-md uppercase tracking-widest text-mkt-primary">
                        Plans and Pricing
                    </span>
                    <h1 className="mt-size-sm font-display-lg-mobile text-display-lg-mobile md:font-display-lg md:text-display-lg text-mkt-on-surface text-balance">
                        Every course on inaraX is $7.
                    </h1>
                    <p className="mt-size-md font-body-lg text-body-lg text-mkt-on-surface-variant text-balance">
                        One payment gives you the whole course: every level, and a certificate when you complete it.
                        Pick the course you want to start with.
                    </p>
                </Reveal>
            </div>

            <div className="pb-size-lg px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto flex flex-col items-center">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-gutter w-full max-w-4xl">
                    {CATALOG_COURSES.map((course) => (
                        <Reveal
                            key={course.id}
                            className="relative overflow-hidden w-full bg-white border border-mkt-outline-variant rounded-xl p-size-md sm:p-size-lg shadow-[0_8px_28px_-4px_rgba(0,80,239,0.35)] hover:shadow-[0_10px_32px_-4px_rgba(0,80,239,0.5)] transition-shadow flex flex-col"
                        >
                            <h2 className="font-headline-lg text-headline-lg text-mkt-on-surface pr-size-xl">
                                {course.title}
                            </h2>
                            <p className="mt-size-xs font-bold text-mkt-primary text-body-lg">{course.tagline}</p>

                            <div className="mt-size-md flex items-baseline gap-2">
                                <span className="font-display-lg text-display-lg text-mkt-primary">{CATALOG_PRICE}</span>
                                <span className="font-body-md text-body-md text-mkt-on-surface-variant">/ one-time payment</span>
                            </div>

                            <dl className="mt-size-md flex flex-col gap-size-xs font-body-md text-body-md">
                                <div className="flex gap-2">
                                    <dt className="font-bold text-mkt-on-surface">For:</dt>
                                    <dd className="text-mkt-on-surface-variant">{course.audience}</dd>
                                </div>
                                <div className="flex gap-2">
                                    <dt className="font-bold text-mkt-on-surface">Level:</dt>
                                    <dd className="text-mkt-on-surface-variant">{course.level}</dd>
                                </div>
                                <div>
                                    <dt className="font-bold text-mkt-on-surface">Focus:</dt>
                                    <dd className="mt-2">
                                        <ul className="list-none pl-0 ml-0 flex flex-wrap gap-2">
                                            {course.focus.map((topic) => (
                                                <li
                                                    key={topic}
                                                    className="rounded-full border border-mkt-primary/20 bg-mkt-primary-container/[0.06] px-3 py-1 text-sm text-mkt-on-surface"
                                                >
                                                    {topic}
                                                </li>
                                            ))}
                                        </ul>
                                    </dd>
                                </div>
                            </dl>
                            <p className="mt-size-md font-body-md text-body-md font-bold text-mkt-on-surface">
                                {catalogCourseFacts(moduleCounts[course.offerPlanKey])}
                            </p>

                            <div className="mt-auto pt-size-lg">
                                {(() => {
                                    const plan = resolvePlan(course.offerPlanKey);
                                    if (plan?.alreadyOwned) {
                                        return (
                                            <div className="mb-size-sm">
                                                <div className="w-full text-center bg-mkt-surface-container border border-mkt-outline-variant text-mkt-on-surface font-label-md text-label-md py-size-sm rounded-full">
                                                    Course purchased
                                                </div>
                                                <Link
                                                    href="/courses"
                                                    className="mt-size-xs block w-full text-center bg-mkt-primary-container text-mkt-on-primary font-label-md text-label-md py-size-sm rounded-full hover:bg-mkt-primary-container/90 transition-all active:scale-95"
                                                >
                                                    Go to course
                                                </Link>
                                            </div>
                                        );
                                    }
                                    return (
                                        <button
                                            type="button"
                                            onClick={() => handleGetStarted(course.offerPlanKey)}
                                            disabled={!PRICING_CHECKOUT_ENABLED}
                                            className="w-full bg-mkt-primary-container text-mkt-on-primary font-label-md text-label-md py-size-sm rounded-full hover:bg-mkt-primary-container/90 soft-glow-shadow transition-all active:scale-95 mb-size-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-mkt-primary-container disabled:active:scale-100"
                                        >
                                            {!PRICING_CHECKOUT_ENABLED
                                                ? PRICING_CHECKOUT_OFF_LABEL
                                                : isSignedIn
                                                  ? `Buy ${course.title} for ${plan?.priceText ?? CATALOG_PRICE}`
                                                  : "Sign in to purchase"}
                                        </button>
                                    );
                                })()}
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal
                    delayMs={100}
                    className="w-full flex flex-wrap justify-center items-center gap-size-md mt-size-lg font-label-md text-label-md text-mkt-outline"
                >
                    {TRUST_STRIP.map((item, index) => (
                        <div key={item.label} className="flex items-center gap-size-md">
                            {index > 0 && <div className="w-1 h-1 rounded-full bg-mkt-outline/50" />}
                            <div className="flex items-center gap-size-xs">
                                <span className="material-symbols-outlined text-[16px]">{item.icon}</span>
                                {item.label}
                            </div>
                        </div>
                    ))}
                </Reveal>

            </div>

            <Reveal className="my-size-xl mx-margin-mobile md:mx-margin-desktop">
                <div className="bg-mkt-inarax-black rounded-xl p-size-md sm:p-size-xl flex flex-col min-[900px]:flex-row items-center gap-size-lg border border-white/10 max-w-container-max mx-auto">
                    <div className="flex-1">
                        <h2 className="text-display-lg-mobile font-display-lg-mobile text-white mb-size-sm text-left">
                            Training a team?
                        </h2>
                        <p className="text-[20px] font-body-lg text-white mb-size-md">
                            Give every employee a path personalized to their role and level, with content built
                            around your goals and progress your L&amp;D team can track.
                        </p>
                        <div className="flex justify-start">
                            <Link
                                href={BOOK_DEMO_HREF}
                                className="inline-flex items-center justify-center border border-white/30 text-white hover:border-white hover:bg-white/10 px-size-xl py-size-md rounded-full text-body-lg font-label-md font-bold transition-all duration-300"
                            >
                                Book a Consultation
                                <span className="material-symbols-outlined ml-size-xs text-[22px]">arrow_forward</span>
                            </Link>
                        </div>
                    </div>
                    <div className="relative w-full min-[900px]:flex-1 h-[220px] min-[900px]:h-[300px] rounded-lg overflow-hidden border border-white/10">
                        <Image
                            alt="Team training together"
                            className="object-cover"
                            src="/images/pricing-train-team.png"
                            fill
                            sizes="(min-width: 900px) 50vw, 100vw"
                        />
                    </div>
                </div>
            </Reveal>

            <div className="px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto">
                <div className="h-[2px] w-full bg-mkt-outline" />
            </div>

            <div className="pb-size-lg pt-size-xl px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto flex flex-col items-center">
                <Reveal delayMs={150} className="mx-auto w-full max-w-[1440px]">
                    <FaqSection
                        heading="Frequently Asked Questions"
                        items={PRICING_FAQS}
                        cardClassName="bg-mkt-surface-container-lowest border border-mkt-outline-variant/30 rounded-lg overflow-hidden transition-all duration-300"
                    />
                </Reveal>

                <div className="mx-auto mt-size-xl max-w-2xl border-t border-mkt-outline-variant/40 pt-size-lg text-center">
                    <p className="font-label-md text-label-md text-mkt-on-surface-variant">
                        All prices are shown in USD. Taxes may apply. Final terms appear at checkout; plans and
                        features can change.
                    </p>
                    <p className="mt-size-sm flex flex-wrap items-center justify-center gap-x-2 gap-y-1 font-label-md text-label-md">
                        <Link href="/terms-of-service" className="font-bold text-mkt-primary underline-offset-2 hover:underline">
                            Terms of Service
                        </Link>
                        <span className="select-none text-mkt-outline" aria-hidden>
                            •
                        </span>
                        <Link href="/privacy-policy" className="font-bold text-mkt-primary underline-offset-2 hover:underline">
                            Privacy Policy
                        </Link>
                        <span className="select-none text-mkt-outline" aria-hidden>
                            •
                        </span>
                        <Link href="/cookie-policy" className="font-bold text-mkt-primary underline-offset-2 hover:underline">
                            Cookie Policy
                        </Link>
                    </p>
                </div>
            </div>
        </section>
    );
}
